import { NgClass } from '@angular/common';
import {
	Component, computed, inject,
	signal
} from '@angular/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { TooltipDirective, IconComponent } from '@skylab-angular-shared/ui';
import { KsefConfigurationStatusItem } from '@skylab-shared/business';
import { KsefConfigurationStatus } from '@skylab-shared/dataModel';
import { IconName } from '@skylab-assets/icons';
import { KsefConfigurationService } from './ksef-configuration.service';

@Component({
	selector: 'skl-ksef-configuration-state-button',
	template: `
		<div class="button-wrapper" (click)="showCreator()" [tooltip]="status().description!">
			<div class="button-icon" [ngClass]="'button-icon' + iconColorClass()">
				<skl-icon size="14px" [name]="iconName()" />
			</div>
			<div class="button-text">
				{{'scenes.shared.ksef-configuration.state-button.title' | translate}}
			</div>
		</div>
	`,
	styles: [`
		:host {
			display: flex;
			justify-content: center;
			flex-direction: column;
		}

		.button-wrapper {
			cursor: pointer;
			display: flex;
			font-weight: 500;
			gap: 4px;
			background-color: var(--line-1-color);
			padding: 7px 10px 7px 8px;
			border-radius: 18px;
			font-size: 14px;
			&:hover {
				background-color: var(--line-2-color);
			}
		}

		.button-icon {
			display: flex;
			justify-content: center;
			flex-direction: column;
			&--inactive {
				color: var(--text-minor-color-light);
			}
			&--success {
				color: var(--success-color);
			}
			&--warning {
				color: var(--warning-color);
			}
			&--error {
				color: var(--error-color);
			}
			&--info {
				color: var(--info-color);
			}
		}

		.button-text {
			color: var(--text-color);
			line-height: 1.2;
		}
	`],
	imports: [NgClass, TranslatePipe, TooltipDirective, IconComponent],
})
export class KsefConfigurationStatusButtonComponent {
	public status = signal<KsefConfigurationStatusItem>({status: KsefConfigurationStatus.INACTIVE, description: '', reason: ''});

	private readonly ksefService = inject(KsefConfigurationService);

	public iconName = computed(() => {
		return ({
			[KsefConfigurationStatus.INACTIVE]: "close-circle-fill",
			[KsefConfigurationStatus.OK]: "check-circle",
			[KsefConfigurationStatus.WARNING]: "exclamation-triangle",
			[KsefConfigurationStatus.ERROR]: "close-circle-fill",
			[KsefConfigurationStatus.BLOCKED]: "close-circle-fill",
			[KsefConfigurationStatus.INFO]: "info",
			[KsefConfigurationStatus.INFO_WARNING]: "exclamation-triangle",
			[KsefConfigurationStatus.INFO_ERROR]: "exclamation-triangle"
		} satisfies Record<KsefConfigurationStatus, IconName>)[this.status().status]
	})

	public iconColorClass = computed(() => {
		return {
			[KsefConfigurationStatus.INACTIVE]: "--inactive",
			[KsefConfigurationStatus.OK]: "--success",
			[KsefConfigurationStatus.WARNING]: "--warning",
			[KsefConfigurationStatus.ERROR]: "--error",
			[KsefConfigurationStatus.BLOCKED]: "--error",
			[KsefConfigurationStatus.INFO]: "--info",
			[KsefConfigurationStatus.INFO_ERROR]: "--warning",
			[KsefConfigurationStatus.INFO_WARNING]: "--warning"
		}[this.status().status]
	})

	public showCreator(): void {
		this.ksefService.showKsefCreatorAsync();
	}

	public ngOnInit(): void {
		this.ksefService.ksefStatus$.subscribe(d => {
			this.status.set(d);
		})
	}
}
